import styled, { keyframes, css } from 'styled-components';
import theme from '../../theme';

/* ----------------- ANIMATIONS ----------------- */
const bounce = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.2); }
`;

const pop = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.3) rotate(15deg); }
  100% { transform: scale(1) rotate(0); }
`;

const floatHeartAnimation = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -200px) scale(0);
    opacity: 0;
  }
`;

/* ----------------- HEADER ----------------- */
export const HeaderWrapper = styled.div`
  position: relative;
  top: 0;
  width: 100%;
  ${props => !props.isMobile && `
    width: calc(100vw - 320px);
  `}
  height: auto;
  display: flex;
  justify-content: center;
  background: ${theme.colors.background};
  z-index: 1603;
  overflow: hidden;
  padding: 0 1rem;
`;

export const HeaderSvg = styled.svg`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: 100%;
  z-index: 0;
  padding: 0.7rem;
  max-width: calc(100% - 1rem);
`;

export const HeaderContent = styled.div`
  position: relative;
  width: 100%;
  max-width: 379px;
  padding: 1rem 0 3rem;
  z-index: 1;
`;

export const HeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 1rem;
`;

export const IconsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
`;

export const LikeIcon = styled.div`
  width: 40px;
  height: 40px;
  margin-top: 0%;
  margin-right: 5%;
  color: ${props => props.$isFavorite ? '#FFF' : 'rgba(255,255,255, 1)'};
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    color: #FFF;
  }

  animation: ${props => props.$animate 
    ? css`${bounce} 0.5s ease` 
    : 'none'};
`;

export const ShareIcon = styled.div`
  width: 40px;
  height: 40px;
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  animation: ${props => props.$animate
    ? css`${pop} 0.3s ease`
    : 'none'};
`;

export const FloatingHeart = styled.div`
  position: absolute;
  pointer-events: none;
  animation: ${floatHeartAnimation} 2s ease-out forwards;
  color: #fff;
  font-size: 24px;
  z-index: 2000;
`;

export const HeaderBottomRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-top: 0.3rem;
  width: 100%;
  align-items: flex-start;
  padding-left: 2rem;
  padding-right: 1rem; /* Ensures left alignment inside the header */
`;

export const FormationName = styled.h1`
  color: white;
  font-size: 1.2rem;
  font-family: 'Gabarito', sans-serif;
  font-weight: 500;
  margin: 0.2rem 0;
  line-height: 1.3;
  white-space: normal; /* Allow text to wrap to multiple lines */
  margin-right: 1.3rem;
  max-width: calc(100% - 1rem);
  transition: all 0.2s ease-in-out;

  /* Conditionally apply line clamp */
  ${props => !props.isExpanded && css`
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

export const SchoolName = styled.span`
  font-size: 0.7rem;
  font-family: 'Gabarito', sans-serif;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.9);
  overflow: hidden;
  margin-bottom: 0.5rem;

  /* One-line clamp + ellipsis */
   display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;

  margin-right: 1rem;
  max-width: calc(100% - 1rem);
`;

export const StickyHeaderContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 1604;
  background-color: ${theme.colors.background};
`;

export const TabsContainer = styled.div`
  position: relative;
  left: 0;
  right: 0;
  z-index: 1603;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  padding: 0.5rem 0;
  background-color: ${theme.colors.background};
`;

export const TabButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 120px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 25px;
  border: 1px solid ${({ active }) => (active ? '#38B6FF' : '#E5E5E5')};
  background: ${({ active }) => (active ? '#DCF3FF' : '#FFF')};
  box-shadow: ${({ active }) => (active ? 'none' : '0px 2px 0px 0px #E5E5E5')};
  cursor: pointer;
  justify-content: center;
  font-size: 15px;
  font-weight: ${({ active }) => (active ? '600' : '500')};
  font-family: 'Gabarito', sans-serif;
  color: ${({ active }) => (active ? '#38B6FF' : '#000')};
  text-align: center;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: none;
  }
`;

export const ScrollableContent = styled.div`
  padding-top: 0;
  padding-left: 1rem;
  padding-right: 1rem;
  padding-bottom: 1rem;
  margin-top: 0;
`;

/* ----------------- OTHER STYLES ----------------- */
export const Container = styled.div`
  width: 100%;
  ${props => !props.isMobile && `
    width: calc(100vw - 320px);
    margin-left: 320px;
  `}
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 1603;
  background-color: ${theme.colors.background};
  font-family: 'Gabarito', sans-serif;
  scrollbar-width: thin;
`;

export const ButtonsContainer = styled.div`
  position: absolute;
  bottom: -7px;
  left: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.7rem;
  justify-content: space-between;
`;

export const CityContainer = styled.div`
  display: flex;
  gap: 0.3rem;
  flex-shrink: 0;
`;

export const CityText = styled.span`
  margin-top: 5px;
  color: white;
  font-size: 0.7rem;
  font-weight: 400;
  font-family: 'Gabarito', sans-serif;
`;

export const ActionButton = styled.a`
  display: flex;
  align-items: center;
  gap: 0px;
  padding: 0px 3px;
  background: white;
  border-radius: 25px;
  text-decoration: none;
  transition: all 0.3s ease;
  border: 1px solid #ddd;
  margin-bottom: 10%;
  margin-left: -2%;

  &:hover {
    background: #f5f5f5;
    transform: translateY(-1px);
  }
`;

export const ButtonIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

export const ButtonIcon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

export const ButtonText = styled.span`
  font-family: 'Gabarito', sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;

  ${ActionButton}:hover & {
    color: #000;
  }
`;

export const Subtitle = styled.span`
  font-family: 'Gabarito', sans-serif;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  margin-left: -10%;
`;

export const ReturnButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.small};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  z-index: 1604;

  img {
    width: 24px;
    height: 24px;
    transform: rotate(180deg);
  }

  &:hover {
    transform: translateX(-3px);
  }
`;

export const TitleColumn = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 1rem;
  flex: 1;
  overflow: hidden;
`;

export const ScoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding-right: 1rem;
`;

export const ScoreTopText = styled.span`
  color: white;
  font-size: 1rem;
  font-family: 'Gabarito', sans-serif;
  /* Réduire l'espacement sous le texte "Match à" */
  margin-bottom: 2px; /* Vous pouvez aussi mettre 0 si besoin */
`;

export const ScoreValue = styled.span`
  color: white;
  font-size: 2.5rem;
  font-family: 'Gabarito', sans-serif;
  font-weight: bold;
  /* Supprimer l'espacement en haut */
  margin-top: -10%;
`;

export const InfoWrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: ${props => {
    // Calcul du padding-top basé sur la hauteur du header
    // Extraire la valeur numérique de headerHeight (enlever "px")
    const headerHeightStr = props.headerHeight || '160px';
    const currentHeight = parseInt(headerHeightStr.replace('px', ''));
    const baseHeight = 160; // Hauteur de base du header (correspondant au minimum calculé)
    const extraHeight = Math.max(0, currentHeight - baseHeight);
    const basePadding = 16; // 1rem en pixels
    const additionalPadding = extraHeight * 0.35; // Utiliser seulement 30% de l'extraHeight pour un ajustement plus subtil
    const totalPadding = basePadding + additionalPadding;
    return `${totalPadding}px 0 0px`;
  }};
`;

export const InfoRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  align-items: center;
  margin: 0.5rem 0;
`;

export const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  text-align: center;
`;


export const InfoText = styled.div`
  font-size: 18px;
  font-family: 'Gabarito', sans-serif;
  color: #000;
  text-align: center;
  width: 70%;
`;

export const Section = styled.div`
  width: 95%;
  margin: 1rem auto;
`;

export const SectionTitle = styled.h2`
  font-size: 20px;
  font-family: 'Gabarito', sans-serif;
  color: #000;
  margin-bottom: 1rem;
  margin-top: 0; /* Add this to remove any top margin */
  font-weight: 600;
  display: inline-flex; /* Add this */
  align-items: center; /* Add this */
`;

export const SectionText = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.4;
  margin: 0.25rem 0;
  text-align: left;
  width: 100%;
  word-break: break-word;
  padding-left: 1em;
  position: relative;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #666;
  }
`;

export const SubsectionTitle = styled.h3`
  font-size: 18px;
  font-family: 'Gabarito', sans-serif;
  color: #333;
  margin: 1rem 0 0.5rem;
  font-weight: 500;
`;

export const GrayBandWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  position: relative;
`;


export const GrayBand = styled.div`
  width: 160%;
  height: 23px;
  border-radius: 25px;
  background: #f2f2f2;
  margin: 1rem 0;
  position: relative;
  left: -30%;
  box-sizing: border-box;

  /* Prevent horizontal scroll */
  @media (max-width: 768px) {
    width: 200%;
    left: -50%;
  }
`;

/* ----------------- DOMAINS MATCHING ----------------- */
export const DomainContainer = styled.div`
  width: 100%;
  // max-width: 95%;
  margin: 0 auto;
  border-radius: 25px;
  background: white;
  border: 1px solid ${theme.colors.gris_clair_default};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem 1rem;
`;


export const DomainItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const DomainIcon = styled.div`
  width: 35px;
  height: 35px;
  background: none;
  border-radius: 4px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const DomainText = styled.div`
  font-size: ${theme.fontSizes.small};
  color: #000;
  font-weight: 400;
`;


export const SectionTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 0.5rem;
`;

export const ImportanceSubtitle = styled.span`
  color: #000;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Gabarito', sans-serif;
  margin: 0.5rem 0;
  text-align: center;
  width: 30%;
`;

export const DomainItemWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.5rem 0;
`;

export const LeftDomainContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  max-width: 70%;
`;

export const RightIconsContainer = styled.div`
  display: flex;
  justify-content: center;  // Center horizontally
  align-items: center;      // Center vertically
  gap: 0.25rem;
  width: 30%;               // Match the remaining width
  height: 100%;             // Take full height of parent
`;

export const ImportanceIcons = styled.div`
  display: flex;
  gap: 0.25rem;
  color: ${theme.colors.primary};
`;

// Add these new styled components to styles.js

// New InfoRow styles
export const InfoRowContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  border-radius: 25px;
  background: white;
  border: 1px solid ${theme.colors.gris_clair_default};
  padding: 1rem;
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: auto;
  width: 100%;
  position: relative;
`;

export const GridCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  height: 100%;
  

  &:nth-child(-n + 2) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.gris_clair_default};
  }

  &:nth-child(odd) {
    border-right: 1px solid ${({ theme }) => theme.colors.gris_clair_default};
  }
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  width: 100%;
  line-height: normal;
  text-align: center;
  width: 100%;
`;

export const InfoTitle = styled.span`
  color: #8D8D8D;
  font-family: 'Gabarito', sans-serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

export const InfoContent = styled.span`
  color: #000;
  font-family: 'Gabarito', sans-serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-align: center;
  width: 100%;
`;

export const SectionIconWrapper = styled.div`
  width: 25px;
  height: 25px;
  margin-right: 0.5rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center; /* Add this */
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const SectionWithIcon = styled.div`
  display: flex;
  align-items: center; /* Keep this */
  margin-bottom: 0.5rem;
  flex-direction: row; /* Add this to enforce horizontal layout */
  flex-wrap: nowrap; /* Add this to prevent wrapping */
`;

// Update the InfoIcon to match the new specs
export const InfoIcon = styled.div`
  width: 25px;
  height: 25px;
  transform: rotate(0.228deg);
  flex-shrink: 0;
  display: flex;
  justify-content: center;

  img {
    width: 100%;
    height: auto;
    object-fit: contain;
  }
`;

// Add the missing styled components
export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px; // Adjust as needed
  padding: 20px;
`;

export const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px; // Adjust as needed
  padding: 20px;
  color: red; // Example error styling
  text-align: center;
`;

export const JoinGroupButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;
  height: 51px;
  flex-shrink: 0;
  border-radius: 25px;
  border: 1px solid ${({ theme }) => theme.colors.gris_clair_default};
  background: #fff;
  box-shadow: ${props => (props.active ? 'none' : `0px 2px 0px 0px ${props.theme.colors.gris_clair_default}`)};
  transform: ${props => (props.active ? 'translateY(3px)' : 'translateY(0)')};
  cursor: pointer;
  width: 100%;
  max-width: 359px;
  margin: 0 auto;
  padding: 0 1rem;
  font-family: 'Gabarito', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #333;
  transition: all 0.15s ease;

  img {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }
`;

export const MajorPrepaButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem 0.5rem 1rem;
  flex-shrink: 0;
  border-radius: 25px;
  border: 1px solid ${({ theme }) => theme.colors.gris_clair_default};
  background: #fff;
  box-shadow: ${props => (props.active ? 'none' : `0px 2px 0px 0px ${props.theme.colors.gris_clair_default}`)};
  transform: ${props => (props.active ? 'translateY(3px)' : 'translateY(0)')};
  cursor: pointer;
  width: 100%;
  max-width: 359px;
  margin: 0 auto;
  font-family: 'Gabarito', sans-serif;
  color: #333;
  transition: all 0.15s ease;

  img {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }
`;

export const MajorPrepaButtonContent = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  gap: 0.1rem;
`;

export const MajorPrepaTitle = styled.span`
  font-weight: 500;
  font-size: 16px;
  color: #333;
`;

export const MajorPrepaText = styled.span`
  font-weight: 400;
  font-size: 14px;
  color: #333;
`;